import {
  Box,
  useTheme,
  FormControlLabel,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid2";
import api from "services/api";

import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useNavigate } from "react-router-dom";
import Overlay from "components/Overlay/Overlay";

const ChangeCategory = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();

  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const [category, setCategory] = useState(overlayData.Category);

  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
  };

  const updateService = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    const dataOld = {
      Assignment: {
        id: overlayData.ServiceID,
        ServiceCategory: overlayData.Category,
      },
    };

    const dataNew = {
      Assignment: {
        id: overlayData.ServiceID,
        ServiceCategory: category,
      },
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/updateCategory", {
        data: allData,
      })
      .then((response) => {
        setIsSavingChanges(false);
        triggerPageRefresh("Service Category Updated Sucessfully", theme);
        setOverlayName("");

        const navBackTo =
          category === "Other"
            ? "otherServices"
            : category === "Translation"
              ? "translations"
              : "interpretations";

        navigate(`/${navBackTo}/`);
      })
      .catch((err) => console.log(err));
  }, [
    category,
    navigate,
    overlayData.Category,
    overlayData.ServiceID,
    setOverlayName,
    theme,
    triggerPageRefresh,
  ]);

  const attemptSave = useCallback(() => {
    if (isSavingChanges) {
      return;
    }

    updateService();
  }, [isSavingChanges, updateService]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (e.key === "Enter" && !e.shiftKey && !isInsideDialog) {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  const footerActions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={() => setOverlayName("")}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={() => setOverlayName("")}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}

      <Box p="0 .5rem" />

      {isMobile ? (
        <Tooltip
          title={isSavingChanges ? "Saving..." : "Save"}
          placement="top"
        >
          <span>
            <IconButton
              disabled={isSavingChanges}
              color="primary"
              onClick={attemptSave}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={isSavingChanges}
          disabled={isSavingChanges}
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          type="submit"
          onClick={attemptSave}
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <Overlay
      id="service-change-category"
      open={true}
      onClose={() => setOverlayName("")}
      title="Change Category"
      maxWidthPx={1200}
      height="fit-content"
      footer={footerActions}
    >
      <Box
        component="form"
        sx={{
          p: "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
          <Grid container spacing={2} width="100%" size={{ xs: 12 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  aria-label="service-category"
                  name="service-category"
                  value={category}
                  onChange={handleCategoryChange}
                  sx={{
                    justifyContent: "space-between",
                    width: "100%",
                    mt: 1,
                  }}
                >
                  <FormControlLabel
                    value="Translation"
                    control={<Radio />}
                    label="Translation"
                    disabled={!isEditable}
                  />
                  <FormControlLabel
                    value="Interpretation"
                    control={<Radio />}
                    label="Interpretation"
                    disabled={!isEditable}
                  />
                  <FormControlLabel
                    value="Other"
                    control={<Radio />}
                    label="Other"
                    disabled={!isEditable}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
      </Box>
    </Overlay>
  );
};

export default ChangeCategory;
